import axios, { HttpStatusCode, isAxiosError } from 'axios'
import { endSession, getStoredToken, isSessionRejection, rejectionReason } from '.'

/**
 * What a REST call to the API rejects with once the API has refused the session. The
 * session is already over and the app is on its way to login; the message is what any
 * caller that shows the error will show.
 */
export class SessionEndedError extends Error {
  constructor() {
    super('Your session has ended. Please log in again.')
    this.name = 'SessionEndedError'
  }
}

type ApiRequestInit = Omit<RequestInit, 'headers'> & { headers?: Record<string, string> }

/**
 * The JSON content type and the session token, unless the caller sets its own. Names are
 * compared case-insensitively so a caller's `authorization` replaces the default instead of
 * being sent alongside it. FormData gets no content type: the browser has to add the boundary.
 */
const withDefaultHeaders = ({ headers = {}, body }: ApiRequestInit): Record<string, string> => {
  const given = new Set(Object.keys(headers).map(name => name.toLowerCase()))
  const defaults: Record<string, string> = {
    ...(!(body instanceof FormData) && { 'Content-Type': 'application/json' }),
    Authorization: `Bearer ${getStoredToken()}`
  }

  return {
    ...Object.fromEntries(Object.entries(defaults).filter(([name]) => !given.has(name.toLowerCase()))),
    ...headers
  }
}

/**
 * `fetch` for calls to the API: sends the default headers and ends the session when the
 * API refuses its token.
 */
export const apiFetch = async (input: RequestInfo | URL, init: ApiRequestInit = {}): Promise<Response> => {
  const response = await fetch(input, { ...init, headers: withDefaultHeaders(init) })

  if (response.status === HttpStatusCode.Unauthorized) {
    const body = await response
      .clone()
      .json()
      .catch(() => null)

    if (isSessionRejection(body)) {
      endSession(rejectionReason(body.code))
      throw new SessionEndedError()
    }
  }

  return response
}

/** Gives every axios call the same treatment as `apiFetch`. Called once at startup. */
export const installAxiosSessionHandling = () =>
  axios.interceptors.response.use(undefined, error => {
    const body =
      isAxiosError(error) && error.response?.status === HttpStatusCode.Unauthorized ? error.response.data : null

    if (isSessionRejection(body)) {
      endSession(rejectionReason(body.code))
      return Promise.reject(new SessionEndedError())
    }

    return Promise.reject(error)
  })
